import {
  getInitials,
  getMembershipPlanType,
  getOtherHouseholdMembers,
  isFrictionlessSignupUser,
} from '@frontend/api-client';
import { convertDateLikeToDateTimeISOString, dateFormatters, dayjs, match } from '@frontend/duck-tape';
import { useMachine } from '@frontend/react';
import type { CommonModalProps } from '@frontend/web-react';
import { Avatar, Button, Icon, Modal, Text, XStack, YStack } from '@frontend/web-react';
import { BuyCreditsModal } from '@frontend/web/components/DomainSpecific/Payment/BuyCreditsModal';
import { InviteHouseholdMemberModal } from '@frontend/web/components/DomainSpecific/Payment/InviteHouseholdMemberModal';
import { CreditHistoryModal } from '@frontend/web/components/DomainSpecific/Settings/CreditHistoryModal';
import { usePricingTerminology } from '@frontend/web/context';
import {
  useExpireInviteMutation,
  useListCardsQuery,
  useMembershipPlans,
  useRemoveMemberFromHouseholdMutation,
  useRetrieveClientQuery,
  useRetrieveCreditsQuery,
  useRetrieveFullLedgerReportQuery,
  useRetrieveHouseholdQuery,
} from '@frontend/web/hooks';
import { handleMutation } from '@frontend/web/utils';
import { useEffect } from 'react';

import { CreditsBreakdownCard } from '../AccountDetails/CreditsBreakdownCard';
import { PaymentMethodsCard } from '../AccountDetails/PaymentMethodsCard';
import { accountDetailsModalMachine } from './accountDetailsModal.machine';
import { HouseholdManagementCard } from './HouseholdManagementCard';

export type AccountDetailsModalProps = CommonModalProps;

export const AccountDetailsModal = ({ isOpen, onClose }: AccountDetailsModalProps) => {
  const [state, send, actorRef] = useMachine(accountDetailsModalMachine);

  // Reset machine to initial state when modal opens
  useEffect(() => {
    if (isOpen) {
      actorRef.start();
    }
  }, [isOpen, actorRef]);
  const { data: client } = useRetrieveClientQuery();
  const { pricingTerminology, showCredits } = usePricingTerminology();
  const { data: credits, refetch: refetchCredits } = useRetrieveCreditsQuery();
  const { data: cards } = useListCardsQuery();
  const { data: household } = useRetrieveHouseholdQuery(client?.household || '', {
    skip: !client?.household,
  });
  const [expireInvite] = useExpireInviteMutation();
  const [removeMemberFromHousehold] = useRemoveMemberFromHouseholdMutation();
  const { isError: isPlansError, isLoading: isPlansLoading, planLookup } = useMembershipPlans();

  const {
    data: ledgerEntries = [],
    isError: ledgerError,
    isLoading: loadingLedger,
    refetch: refetchLedger,
  } = useRetrieveFullLedgerReportQuery({
    end: convertDateLikeToDateTimeISOString(dayjs().startOf('minute')),
    start: convertDateLikeToDateTimeISOString(dayjs().startOf('minute').subtract(2, 'month')),
  });

  const membershipPlanType = client ? getMembershipPlanType(client) : undefined;
  const membershipPlan = membershipPlanType ? planLookup[membershipPlanType] : undefined;

  // Check for pending subscription change
  const pendingSubscriptionChange = client?.pendingSubscriptionChange;

  // Get target plan details if there's a pending change
  const targetPlanType = pendingSubscriptionChange
    ? getMembershipPlanType({ ...client, plan: pendingSubscriptionChange.targetPlan })
    : undefined;
  const targetPlan = targetPlanType ? planLookup[targetPlanType] : undefined;

  const membershipPlanLabel = isPlansLoading
    ? 'Loading plan...'
    : isPlansError
      ? 'Your plan took a duck dive.'
      : membershipPlan
        ? `${membershipPlan.label} plan${pendingSubscriptionChange ? ` → ${pendingSubscriptionChange.targetPlan.title}` : ''}`
        : '';

  const handleClose = () => {
    onClose();
  };

  const handleBack = () => {
    send({ type: 'back' });
  };

  const handleBuyCredits = () => {
    send({ type: 'showBuyCredits' });
  };

  const handleCreditHistory = () => {
    refetchLedger();
    send({ type: 'showCreditHistory' });
  };

  const handleInviteMember = () => {
    send({ type: 'showInviteMember' });
  };

  const handleRemoveMember = (memberId: string, memberName: string) => {
    send({ memberId, memberName, type: 'showRemoveMember' });
  };

  const handleRemoveInvite = (inviteId: string, inviteName: string) => {
    send({ inviteId, inviteName, type: 'showRemoveInvite' });
  };

  const handleConfirmRemoveMember = () => {
    const { memberToRemove } = state.context;
    if (memberToRemove.id && client?.household && household?.id) {
      handleMutation({
        mutation: () =>
          removeMemberFromHousehold({
            clientId: memberToRemove.id,
            householdId: client.household!,
          }).unwrap(),
        onSuccess: () => {
          send({ type: 'back' });
        },
        successMessage: 'Member removed successfully',
      });
    }
  };

  const handleConfirmRemoveInvite = () => {
    const { inviteToRemove } = state.context;
    if (inviteToRemove.id) {
      handleMutation({
        mutation: () => expireInvite(inviteToRemove.id).unwrap(),
        onSuccess: () => {
          send({ type: 'back' });
        },
        successMessage: 'Invite removed successfully',
      });
    }
  };

  const renderModalContent = () => {
    return match(state.value)
      .with('creditHistory', () => (
        <CreditHistoryModal
          closeModal={handleBack}
          isError={ledgerError}
          isLoading={loadingLedger}
          isOpen={true}
          ledgerEntries={ledgerEntries}
          pricingTerminology={pricingTerminology}
          showBackButton={true}
          subscriptionRenewalCredits={credits?.subscriptionRenewalCredits}
        />
      ))
      .with('buyCredits', () => (
        <BuyCreditsModal
          closeModal={handleBack}
          isOpen={true}
          onCreditsPurchased={() => {
            refetchCredits();
            refetchLedger();
          }}
        />
      ))
      .with('inviteMember', () =>
        household?.id ? (
          <InviteHouseholdMemberModal
            householdId={household.id}
            isOpen={true}
            onClose={handleBack}
            showBackButton={true}
          />
        ) : null,
      )
      .with('removeMemberConfirmation', () => (
        <Modal isOpen={true} onClose={handleBack} size="md">
          <YStack className="gap-xl">
            <XStack className="justify-between w-full items-center">
              <XStack />
              <Text type="h3">Remove member</Text>
              <Icon name="IconX" onClick={handleBack} />
            </XStack>

            <XStack className="gap-md">
              <Icon color="danger" name="IconInfoCircleFilled" size={40} />
              <YStack>
                <Text type="h3">Remove {state.context.memberToRemove.name}</Text>
                <Text color="secondary" type="p-sm">
                  Are you sure you want to remove {state.context.memberToRemove.name} from your household?
                </Text>
              </YStack>
            </XStack>

            <Button label="Remove member" onClick={handleConfirmRemoveMember} variant="destructive" />
          </YStack>
        </Modal>
      ))
      .with('removeInviteConfirmation', () => (
        <Modal isOpen={true} onClose={handleBack} size="md">
          <YStack className="gap-xl">
            <XStack className="justify-between w-full items-center">
              <XStack />
              <Text type="h3">Remove invite</Text>
              <Icon name="IconX" onClick={handleBack} />
            </XStack>

            <XStack className="gap-md">
              <Icon color="danger" name="IconInfoCircleFilled" size={40} />
              <YStack>
                <Text type="h3">Remove invite for {state.context.inviteToRemove.name}</Text>
                <Text color="secondary" type="p-sm">
                  Are you sure you want to remove the invite for {state.context.inviteToRemove.name}?
                </Text>
              </YStack>
            </XStack>

            <Button label="Remove invite" onClick={handleConfirmRemoveInvite} variant="destructive" />
          </YStack>
        </Modal>
      ))
      .otherwise(() => (
        // Default case for 'accountDetails' and any other states
        <Modal
          Header={
            <XStack className="bg-surfaceSecondary w-full justify-end px-md pt-md pb-none">
              <Icon
                accessibilityLabel="Close account details modal"
                canFocus={false}
                name="IconX"
                onClick={handleClose}
                size="lg"
              />
            </XStack>
          }
          bodyClassName="bg-surfaceSecondary"
          isOpen={true}
          onClose={handleClose}
        >
          <YStack className="gap-md max-w-2xl">
            {/* Pending Subscription Change Notice */}
            {pendingSubscriptionChange ? (
              <YStack bg="surfacePrimary" className="rounded-lg p-md gap-sm">
                <Text color="primary" type="h5">
                  Scheduled Plan Change
                </Text>
                <Text color="secondary" type="p-sm">
                  Your plan will change to {pendingSubscriptionChange.targetPlan.title} on{' '}
                  {dateFormatters['MMM D'](pendingSubscriptionChange.effectiveAt)}.{' '}
                  {pendingSubscriptionChange.currentPlan.maxAccounts > pendingSubscriptionChange.targetPlan.maxAccounts
                    ? 'Your partner will be removed and lose access to shared tasks at that time.'
                    : ''}
                </Text>
              </YStack>
            ) : null}

            {/* Membership Section */}
            <YStack className="gap-md">
              <YStack className="gap-sm">
                <Text type="h2">{membershipPlanLabel}</Text>
                {client?.subscriptionEnd ? (
                  <XStack className="gap-xs flex-wrap">
                    <Text color="secondary" type="p-sm">
                      {showCredits
                        ? `Renews ${targetPlan?.creditsPerMonth || membershipPlan?.creditsPerMonth} ${pricingTerminology.unit_term_plural} for $${((targetPlan?.price || membershipPlan?.price || 0) / 100).toFixed(0)} on ${dateFormatters['MMM D'](client.subscriptionEnd)}`
                        : `Renews for $${((targetPlan?.price || membershipPlan?.price || 0) / 100).toFixed(0)} on ${dateFormatters['MMM D'](client.subscriptionEnd)}`}
                    </Text>
                  </XStack>
                ) : null}
                {/* Household sharing information */}
                {household && client && getOtherHouseholdMembers(client.id, household).length > 0 ? (
                  <XStack className="gap-xs items-center flex-wrap">
                    <Text color="secondary" type="p-sm">
                      Shares with{' '}
                    </Text>
                    {getOtherHouseholdMembers(client.id, household).map((member, index, array) => (
                      <XStack className="gap-xs items-center" key={member.id}>
                        <Text color="secondary" type="p-sm">
                          {member.name}
                          {index < array.length - 1 ? ',' : ''}
                        </Text>
                        <Avatar
                          accessibilityLabel={`${member.name} avatar`}
                          initials={getInitials({
                            firstName: member.name?.split(' ')[0] || '',
                            lastName: member.lastName || member.name?.split(' ').slice(1).join(' ') || '',
                            nickname: '',
                          })}
                          size="sm"
                        />
                      </XStack>
                    ))}
                  </XStack>
                ) : null}
              </YStack>

              {!isFrictionlessSignupUser(client) ? (
                <XStack className="gap-sm">
                  <Button
                    label={pricingTerminology.add_button}
                    leftIconName="IconPlus"
                    onClick={handleBuyCredits}
                    variant="primary"
                  />
                </XStack>
              ) : null}
            </YStack>

            {/* Credits Card */}
            <CreditsBreakdownCard credits={credits} onViewHistory={handleCreditHistory} />

            {/* Payment Methods Card */}
            <PaymentMethodsCard cards={cards} />

            {/* Household Management Card */}
            <HouseholdManagementCard
              client={client}
              household={household}
              onInviteMember={handleInviteMember}
              onRemoveInvite={handleRemoveInvite}
              onRemoveMember={handleRemoveMember}
            />
          </YStack>
        </Modal>
      ));
  };

  return isOpen ? renderModalContent() : null;
};
